import React from 'react'
import axios from 'axios'

import { ValidationIcon, LoadingIndicator } from 't3-widgets'

import pageStyles from '../components/pages/pages.module.css'
import tooltipStyle from '../components/pages/tooltips.module.css'

const noCacheHeaders = {
    'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
    Expires: 0,
    'Surrogate-Control': 'no-store'
}

function getTranslation(translations, sc) {
    let translation = '#' + sc

    if (translations.length > 0) {
        translations[0].find(function (t) {
            if (t.stringCode === sc) {
                translation = t.translation
            }
        })
    }

    return translation
}

function loadContent(condition, content) {
    return (
        <LoadingIndicator isLoading={!condition} key="loading-animation-visible">
            {content}
        </LoadingIndicator>
    )
}

function getTooltip(header, description, image) {
    const tooltip = (
        <div className={tooltipStyle['tooltip-container']}>
            <div className={tooltipStyle['header']}>{header}</div>
            <div className={tooltipStyle['description']}>{description}</div>
            {image ? (
                <div className={tooltipStyle['image-container']}>
                    <img className={tooltipStyle['image']} src={image} alt="image" />
                </div>
            ) : (
                <div />
            )}
        </div>
    )

    return tooltip
}

function getValidator(trigger, condition, defaultValue) {
    const value = typeof defaultValue === 'undefined' ? true : defaultValue

    return trigger ? condition : value
}

function loadingProcess(condition) {
    return (
        <div className={!condition ? pageStyles['disable-background'] : ''} width="100%">
            <LoadingIndicator isLoading={!condition} key="loading-animation-visible" />
        </div>
    )
}

function isSingleElementArray(arr) {
    return arr.length === 1
}

function validationChecker(condition) {
    return (
        <div className={pageStyles['validated-input-checker']}>
            <div className={pageStyles['vertical-align-container']}>
                <div className={pageStyles['vertical-align-item']}>
                    {condition ? <ValidationIcon width={25} inheritColor /> : <div />}
                </div>
            </div>
        </div>
    )
}

function fetch(options) {
    const defaultOptions = {
        data: {},
        headers: {
            'Content-Type': 'application/json',
            ...noCacheHeaders
        },
        hostname: window.location.hostname,
        method: 'GET',
        url: ''
    }

    const targetOptions = {
        data: options.data || defaultOptions.data,
        headers: {
            ...defaultOptions.headers,
            ...(options.headers || {})
        },
        hostname: defaultOptions.hostname,
        method: options.method || defaultOptions.method,
        url: options.url || defaultOptions.url,
        timeout: options.timeout || null
    }

    return new Promise((resolve, reject) => {
        axios(targetOptions)
            .then(function (response) {
                resolve(response)
            })
            .catch(function (error) {
                reject(error.response)
            })
    })
}

function verifyCustomer(activationCode, customerNumber) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        method: 'POST',
        url: '/api/verifyCustomer'
    }

    return fetch(options)
}

function activateDevice(activationCode, customerNumber, body) {
    const options = {
        data: body,
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        method: 'POST',
        url: '/api/activateUnit'
    }
    return fetch(options)
}

function verifyDak(activationCode, customerNumber, body) {
    const options = {
        data: body,
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        method: 'POST',
        url: '/api/verifyDak'
    }

    return fetch(options)
}

function verifyDevice(activationCode, customerNumber, deviceSerialNumber) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        url: `/api/deviceActivationOptions/${deviceSerialNumber}?isStandardInstallerProfile=true`
    }

    return fetch(options)
}

function rmaDevices(activationCode, customerNumber, deviceSerialNumber) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        url: '/api/rmaDevices/' + deviceSerialNumber
    }

    return fetch(options)
}

function showUnit(activationCode, customerNumber, deviceSerialNumber) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        url: '/api/showUnit/' + deviceSerialNumber
    }

    return fetch(options)
}

function getBrands(activationCode, customerNumber) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        timeout: 1000 * 15,
        url: '/api/brands'
    }

    return fetch(options)
}

function getModels(activationCode, customerNumber, brandId) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        timeout: 1000 * 15,
        url: `/api/models?brandId=${brandId}`
    }

    return fetch(options)
}

function getGenerations(activationCode, customerNumber, modelId) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        timeout: 1000 * 15,
        url: `/api/generations?modelId=${modelId}`
    }

    return fetch(options)
}

function getTirematicsActivationStatus(activationCode, customerNumber, licensePlate, fleetcareContractNumber) {
    const options = {
        headers: {
            'Content-Type': 'application/json',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        timeout: 1000 * 15,
        url: `/api/tirematicsActivationStatus?licensePlate=${licensePlate}&fleetcareContractNumber=${fleetcareContractNumber}`
    }

    return fetch(options)
}

function getYearTo(yearTo, intl) {
    return yearTo.includes('ap_') ? intl.translate(yearTo) : yearTo
}

function getActivatedUnitsHistory(activationCode, customerNumber) {
    const options = {
        data: {},
        headers: {
            'Content-Type': 'text/csv',
            'X-Customer-Code': customerNumber + '-' + activationCode
        },
        hostname: window.location.hostname,
        method: 'GET',
        url: '/api/activatedUnitsHistory'
    }
    return new Promise((resolve, reject) => {
        axios(options)
            .then(function (response) {
                resolve(response)
            })
            .catch(function (error) {
                reject(error.response)
            })
    })
}

function isURLParamExists(URLParam) {
    return new URLSearchParams(window.location.search).get(URLParam) !== null
}

function getValueFromURLParam(URLParam) {
    return new URLSearchParams(window.location.search).get(URLParam)
}

export {
    fetch,
    getTranslation,
    loadContent,
    getTooltip,
    getValidator,
    loadingProcess,
    isSingleElementArray,
    validationChecker,
    activateDevice,
    verifyCustomer,
    verifyDak,
    verifyDevice,
    rmaDevices,
    showUnit,
    getActivatedUnitsHistory,
    getBrands,
    getGenerations,
    getModels,
    getValueFromURLParam,
    getYearTo,
    isURLParamExists,
    getTirematicsActivationStatus
}
